"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

const COOKIE_CONSENT_KEY = "cookie-consent";
const COOKIE_PREFERENCES_KEY = "cookie-preferences";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Mark as mounted (client-side only)
    setMounted(true);
    
    // Check if user has already made a choice
    if (typeof window !== "undefined") {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!consent) {
        // Show banner after a short delay for better UX
        setTimeout(() => setShowBanner(true), 500);
      } else {
        // Apply saved preferences
        applyCookiePreferences(JSON.parse(consent));
      }
    }
  }, []);

  const applyCookiePreferences = (preferences) => {
    if (typeof window === "undefined") return;
    
    // Store preferences for use by other components
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    
    // Initialize analytics and error tracking if consented
    // Only initialize PostHog in production
    if (preferences.analytics && process.env.NODE_ENV === 'production') {
      // Dynamically import and initialize PostHog if not already loaded
      import("posthog-js").then((posthog) => {
        if (!posthog.default.__loaded && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          posthog.default.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
            api_host: "/ingest",
            ui_host: "https://us.posthog.com",
            defaults: '2025-05-24',
            capture_exceptions: true,
            debug: false, // No debug in production
          });
        }
      }).catch(() => {
        // PostHog may already be initialized via instrumentation
        console.log("PostHog initialization handled by instrumentation");
      });
    }
    
    // Dispatch custom event so other components can react
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", { detail: preferences })
    );
  };

  const handleAcceptAll = () => {
    if (typeof window === "undefined") return;
    const preferences = {
      essential: true,
      analytics: true,
      errorTracking: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    applyCookiePreferences(preferences);
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    if (typeof window === "undefined") return;
    const preferences = {
      essential: true,
      analytics: false,
      errorTracking: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    applyCookiePreferences(preferences);
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = (preferences) => {
    if (typeof window === "undefined") return;
    const fullPreferences = {
      ...preferences,
      essential: true, // Essential cookies are always enabled
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(fullPreferences));
    applyCookiePreferences(fullPreferences);
    setShowPreferences(false);
    setShowBanner(false);
  };

  // Don't render anything until mounted (prevents SSR issues)
  if (!mounted) {
    return null;
  }

  if (!showBanner && !showPreferences) {
    // Show a small button to manage preferences
    if (typeof window !== "undefined") {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (consent) {
        return (
          <button
            onClick={() => setShowPreferences(true)}
            className="fixed bottom-4 right-4 z-50 px-4 py-2 text-xs bg-gray-800 text-white rounded-lg shadow-lg hover:bg-gray-700 transition-colors"
            aria-label="Manage cookie preferences"
          >
            Cookie Preferences
          </button>
        );
      }
    }
    return null;
  }

  if (showPreferences) {
    return <CookiePreferencesModal onSave={handleSavePreferences} onClose={() => setShowPreferences(false)} />;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="container mx-auto px-4 py-4 md:py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                We use cookies
              </h3>
              <p className="text-sm text-gray-600">
                We use cookies to enhance your browsing experience, analyze site traffic, and assist with error tracking. 
                Essential cookies (authentication, security, payment processing) are required for the site to function.{" "}
                <Link
                  href="/cookie-policy"
                  className="text-[#4059BF] hover:underline"
                >
                  Learn more
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:flex-shrink-0">
              <button
                onClick={handleRejectNonEssential}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Reject Non-Essential
              </button>
              <button
                onClick={handleCustomize}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Customize
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: 'rgba(64, 89, 191, 1)' }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgb(11, 62, 151)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(64, 89, 191, 1)';
                }}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CookiePreferencesModal({ onSave, onClose }) {
  const [preferences, setPreferences] = useState({
    analytics: false,
    errorTracking: false,
  });

  useEffect(() => {
    // Load saved preferences only on client-side
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setPreferences({
            analytics: parsed.analytics ?? false,
            errorTracking: parsed.errorTracking ?? false,
          });
        } catch (e) {
          // Invalid JSON, use defaults
        }
      }
    }
  }, []);

  const handleSave = () => {
    onSave(preferences);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Cookie Preferences
          </h2>
          
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Essential Cookies
                  </h3>
                  <p className="text-sm text-gray-600">
                    Required for the website to function. These include authentication, security, and payment processing cookies (Stripe) needed for checkout.
                  </p>
                </div>
                <span className="ml-4 text-sm font-medium text-gray-500">
                  Always Active
                </span>
              </div>
            </div>

            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Analytics Cookies
                  </h3>
                  <p className="text-sm text-gray-600">
                    Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) =>
                      setPreferences({ ...preferences, analytics: e.target.checked })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4059BF]"></div>
                </label>
              </div>
            </div>

            <div className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Error Tracking Cookies
                  </h3>
                  <p className="text-sm text-gray-600">
                    Help us identify and fix technical issues by collecting error information.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={preferences.errorTracking}
                    onChange={(e) =>
                      setPreferences({
                        ...preferences,
                        errorTracking: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#4059BF]"></div>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: 'rgba(64, 89, 191, 1)' }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgb(11, 62, 151)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(64, 89, 191, 1)';
              }}
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
